import { useState } from 'react';
import { IconTrash } from '@tabler/icons-react';
import styled from 'styled-components';
import DeleteCustomThemeDialog from './DeleteCustomThemeDialog';
import EditableCustomThemeLabel from './EditableCustomThemeLabel';
import { DEFAULT_CUSTOM_THEME_DATA } from 'builder/util/constants';
import { StyledHSBar, StyledLink } from 'builder/util/StyledComponents';
import { Button } from '@uhg-harmony/uitk-react';
import { defaultTheme } from 'shared/theme/theme';

const StyledLinkWrapper = styled.div`
  display: flex;
  padding: 0 0.5rem;
  justify-content: space-between;
  width: -webkit-fill-available;
`;

const StyledIconTrash = styled(IconTrash)`
  :hover {
    background-color: #d9f6fa;
    border-radius: 5px;
    outline: solid 1px #c7c7c7;
  }
`;

const ThemeColorCard = styled.div`
  border-style: solid;
  border-radius: 0.375rem;
  border-width: thin;
  font-family: OptumSans, sans-serif;
  border-color: ${defaultTheme.colors.aux.lightGrey};
  margin: 0.5rem;
`;

const ThemeSelectButton = styled(Button)<{ themeEnabled: boolean }>`
  line-height: 0.25;
  font-family: OptumSans, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  margin: 0.5rem;
  padding: 0.25rem 1rem;
  background: ${p => (p.themeEnabled ? 'rgb(25, 110, 207)' : 'transparent')};
  color: ${p => (p.themeEnabled ? 'rgb(255, 255, 255)' : 'rgb(0, 38, 119)')};
`;

const HSBarWrapper = styled.div`
  width: 100%;
  margin-bottom: -0.375rem;
  #hsbar {
    border-bottom-right-radius: 0.36rem;
    border-bottom-left-radius: 0.36rem;
  }
`;

interface CustomThemeCardProps {
  themeKey: number;
  themeName: string;
  site: Site;
  pages?: Array<Page>;
  enabledTheme?: string;
  isDeleteThemeDisabled?: boolean;
  updateSite: (site: Site) => void;
  updateCurrentPage?: (pageVersion: PageVersion) => void;
  openColorPanel: (e: React.MouseEvent<any>) => void;
  selectCustomTheme: (customThemeButtonId: string) => void;
  decrementCustomThemeCount?: () => void;
  handleDeleteCustomTheme?: (id: number) => void;
  handleOpenColorPanel?: (e: React.MouseEvent<any>) => void;
}

const CustomThemeCard = (props: CustomThemeCardProps) => {
  const {
    themeKey,
    themeName,
    site,
    enabledTheme,
    isDeleteThemeDisabled = false,
    updateSite,
    selectCustomTheme,
    handleDeleteCustomTheme,
    handleOpenColorPanel,
  } = props;

  const [
    deleteCustomThemeDialogOpen,
    setDeleteCustomThemeDialogOpen,
  ] = useState(false);

  const openDeleteThemeDialog = () => {
    setDeleteCustomThemeDialogOpen(true);
  };

  const fetchColorBarData = () => {
    const values = [38, 32, 24, 16];
    const themeColors =
      site.theme[`customTheme${themeKey}`]?.colors?.custom ||
      site.theme.colors.custom ||
      [];

    return values.map((value, index) => ({
      value,
      color:
        themeColors[index]?.value ??
        DEFAULT_CUSTOM_THEME_DATA.colors.custom[index].value,
    }));
  };

  return (
    <>
      <ThemeColorCard>
        <EditableCustomThemeLabel
          themeKey={themeKey}
          themeName={themeName}
          site={site}
          updateSite={updateSite}
        />
        <ThemeSelectButton
          id={`custom-theme-button-${themeKey}`}
          data-test-id={`custom-theme-button-${themeKey}`}
          variant={
            enabledTheme === `customTheme${themeKey}` ? 'secondary' : 'tertiary'
          }
          onPress={() => selectCustomTheme(`customTheme${themeKey}`)}
          disabled={!site.access.edit}
          size="m"
          themeEnabled={enabledTheme === `customTheme${themeKey}`}
        >
          {enabledTheme === `customTheme${themeKey}`
            ? 'Theme in use'
            : 'Use this theme'}
        </ThemeSelectButton>
        <StyledLinkWrapper>
          <StyledLink
            id={`edit-colors-link-${themeKey}`}
            data-test-id={`edit-colors-link-${themeKey}`}
            style={{ width: 'fit-content' }}
            onClick={handleOpenColorPanel}
          >
            Edit colors{' >'}
          </StyledLink>
          {!isDeleteThemeDisabled && (
            <StyledLink
              style={{ width: 'fit-content', color: 'red' }}
              data-test-id="delete-custom-theme-button"
              onClick={openDeleteThemeDialog}
            >
              <StyledIconTrash size={18} stroke={2} />
            </StyledLink>
          )}
        </StyledLinkWrapper>
        <HSBarWrapper>
          <StyledHSBar
            data-test-id="color-bar"
            height="1.5rem"
            data={fetchColorBarData()}
          />
        </HSBarWrapper>
      </ThemeColorCard>
      {deleteCustomThemeDialogOpen && (
        <DeleteCustomThemeDialog
          deleteCustomThemeDialogOpen={deleteCustomThemeDialogOpen}
          setDeleteCustomThemeDialogOpen={setDeleteCustomThemeDialogOpen}
          handleDeleteCustomTheme={handleDeleteCustomTheme}
          themeKey={themeKey}
          themeName={themeName}
        />
      )}
    </>
  );
};

export default CustomThemeCard;
